import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import ExamSetListTable from "./ExamSetListTable";
import type { IExamSetListParams } from "@/apis/exam_sets/getExamSetList";
import { getPaginationInfo } from "@/types/pagination";
import { useQueryGetExamSetList } from "@/apis/exam_sets/getExamSetList";

const ExamSetListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState<IExamSetListParams>({
    page: 1,
    limit: 10,
  });

  // React Query API 호출
  const { data, isLoading, error } = useQueryGetExamSetList(searchParams);

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      title: searchTerm,
      page: 1,
    }));
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 시험 세트 추가 페이지로 이동
  const handleAddExamSet = () => {
    navigate("/exam_sets/create");
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Set List</Heading>
        <Text className="text-zinc-500">
          View all exam sets. Use the search to find specific sets.
        </Text>

        <div className="flex justify-between mt-4">
          <Button onClick={handleAddExamSet}>
            <PlusIcon />
            Add Exam Set
          </Button>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="Search by title..."
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Exam Set List Table Component */}
        <ExamSetListTable
          data={data?.items || []}
          loading={isLoading}
          total={data?.totalCount || 0}
          page={paginationInfo?.currentPage || 1}
          pageSize={searchParams.limit || 10}
          onPageChange={(page) => handlePageChange(page)}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default ExamSetListPage;
